import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CenterSpinner, EmptyState } from "@/components/common/States";

export default function AdminPromotions() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-promotions"], queryFn: () => api("/admin/promotions") });
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);

  const create = async () => {
    try {
      await api("/admin/promotions", { method: "POST", json: { code, discount_percent: Number(pct), active: true } });
      toast.success("Promotion created");
      setOpen(false);
      setCode("");
      qc.invalidateQueries({ queryKey: ["admin-promotions"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggle = async (p, active) => {
    try {
      await api(`/admin/promotions/${p.promo_id}`, { method: "PATCH", json: { active } });
      qc.invalidateQueries({ queryKey: ["admin-promotions"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <StaffShell
      items={adminNav}
      roleLabel="Admin"
      title="Promotions"
      headerRight={<Button className="rounded-full" onClick={() => setOpen(true)} data-testid="add-promo-button"><Plus className="mr-1 h-4 w-4" /> New code</Button>}
    >
      {isLoading ? (
        <CenterSpinner />
      ) : !data?.length ? (
        <EmptyState title="No promo codes yet" action={<Button className="rounded-full" onClick={() => setOpen(true)}>Create one</Button>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((p) => (
            <Card key={p.promo_id} className="flex items-center justify-between rounded-2xl p-5" data-testid={`promo-${p.promo_id}`}>
              <div>
                <p className="font-display text-lg">{p.code}</p>
                <Badge variant="secondary" className="mt-1 rounded-full font-normal">{p.discount_percent}% off</Badge>
              </div>
              <Switch checked={p.active} onCheckedChange={(v) => toggle(p, v)} data-testid={`promo-toggle-${p.promo_id}`} />
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New promotion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SUMMER15" data-testid="promo-code-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Discount (%)</Label>
              <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="promo-pct-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={create} disabled={!code} data-testid="save-promo-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
